import { useEffect, useState, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { useLanguage } from "@/hooks/useLanguage";

/**
 * GalleryEntrance — premium "gallery threshold" overlay shown once per
 * browser session before the homepage hero is revealed.
 *
 * Design intent:
 *   • Black gallery wall with subtle warm depth, not a loader.
 *   • Bigger, navbar-matched wordmark.
 *   • Rounded pill CTA matching site language.
 *   • On enter → pillar-aperture reveal: the black overlay irises closed
 *     through a rounded pill shape while a warm light bloom grows, then
 *     dissolves — the hero (already mounted behind) is revealed.
 *   • Reduced-motion users get a flat, fast fade. Hard 7.5s safety timer.
 *   • Skipped on every subsequent route in the same session.
 */

const SESSION_KEY = "frankPistoriusEnteredShowroom";
const LEGACY_SESSION_KEY = "fp.entrance.entered.v1";

const COPY = {
  en: { line1: "Lighting Sales Agency",        cta: "Enter the Showroom" },
  de: { line1: "Handelsagentur für Licht",     cta: "Showroom betreten" },
  it: { line1: "Agenzia di Illuminazione",     cta: "Entra nello showroom" },
  es: { line1: "Agencia de Iluminación",       cta: "Entrar al showroom" },
  nl: { line1: "Verlichtingsagentuur",         cta: "Betreed de showroom" },
} as const;

function isHomeRoot(pathname: string): boolean {
  const parts = pathname.replace(/^\/+/, "").replace(/\/+$/, "").split("/");
  if (parts.length === 0 || parts[0] === "") return true;
  if (parts.length === 1 && ["de","en","it","es","nl"].includes(parts[0])) return true;
  return false;
}

function alreadyEntered(): boolean {
  try {
    return sessionStorage.getItem(SESSION_KEY) === "1"
        || sessionStorage.getItem(LEGACY_SESSION_KEY) === "1";
  } catch { return false; }
}

const GalleryEntrance = () => {
  const location = useLocation();
  const { language } = useLanguage();
  const reduced = useReducedMotion();

  const [mounted, setMounted] = useState(() => {
    if (typeof window === "undefined") return false;
    if (alreadyEntered()) return false;
    return isHomeRoot(window.location.pathname);
  });
  const [stage, setStage] = useState<"idle" | "ready" | "exiting">("idle");
  const exitTimer = useRef<number | null>(null);
  const safetyTimer = useRef<number | null>(null);

  const copy = COPY[language] ?? COPY.en;

  // Lock background scroll while overlay is visible.
  useEffect(() => {
    if (!mounted) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [mounted]);

  // Trigger reveal animation on next frame; install hard safety timeout.
  useEffect(() => {
    if (!mounted) return;
    const r1 = requestAnimationFrame(() => {
      requestAnimationFrame(() => setStage("ready"));
    });
    safetyTimer.current = window.setTimeout(() => handleEnter(), 9000);
    return () => {
      cancelAnimationFrame(r1);
      if (safetyTimer.current) window.clearTimeout(safetyTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted]);

  const handleEnter = useCallback(() => {
    setStage((s) => (s === "exiting" ? s : "exiting"));
    try { sessionStorage.setItem(SESSION_KEY, "1"); } catch { /* ignore */ }
    // Premium opt-in cue — no-op if user hasn't enabled sound.
    import("@/lib/audioBus")
      .then(({ audioBus }) => audioBus.play("transition", { throttleMs: 800 }))
      .catch(() => {});
    if (safetyTimer.current) {
      window.clearTimeout(safetyTimer.current);
      safetyTimer.current = null;
    }
    // Tell the hero to begin revealing once the centre aperture has opened
    // enough to show the video/poster underneath. This drives the hero
    // line-mask copy reveal so it runs in concert with the visual reveal.
    const heroRevealDelay = reduced ? 200 : 900;
    window.setTimeout(() => {
      try { window.dispatchEvent(new CustomEvent("fp:hero-reveal")); } catch { /* ignore */ }
    }, heroRevealDelay);
    const exitDuration = reduced ? 360 : 2400;
    exitTimer.current = window.setTimeout(() => setMounted(false), exitDuration);
  }, [reduced]);

  // Unmount overlay if route changes while still showing.
  useEffect(() => {
    if (!mounted) return;
    if (!isHomeRoot(location.pathname)) {
      try { sessionStorage.setItem(SESSION_KEY, "1"); } catch { /* ignore */ }
      setMounted(false);
    }
  }, [location.pathname, mounted]);

  // Global key handler.
  useEffect(() => {
    if (!mounted) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " " || e.key === "Escape") {
        e.preventDefault();
        handleEnter();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mounted, handleEnter]);

  useEffect(() => () => {
    if (exitTimer.current) window.clearTimeout(exitTimer.current);
  }, []);

  if (!mounted) return null;

  const visible = stage === "ready";
  const exiting = stage === "exiting";
  const ease = "cubic-bezier(0.16, 1, 0.3, 1)";
  const easeSoft = "cubic-bezier(0.22, 1, 0.36, 1)";

  // ── Reverse-vignette aperture ──
  // The black overlay is rendered visible everywhere by default. On exit,
  // a soft transparent "hole" opens in the centre and expands outward,
  // revealing the hero from the middle while the outer edges remain black
  // (soft vignette) until the very end. Implemented via CSS mask so the
  // transparent area literally cuts through the black layer — not a fade.
  //
  // Mask is a radial gradient: transparent at the centre, opaque at the
  // edge. We animate the inner radius from ~0% to ~150% so the aperture
  // grows past the viewport corners. Safari needs -webkit-mask-* fallback.
  const maskInner = exiting && !reduced ? "78%" : "0%";
  const maskOuter = exiting && !reduced ? "150%" : "0.01%";
  const maskImage =
    `radial-gradient(circle at 50% 50%, transparent 0%, transparent ${maskInner}, #000 ${maskOuter})`;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-hidden={exiting ? "true" : "false"}
      aria-label="Frank Pistorius — gallery entrance"
      onClick={handleEnter}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2147483646,
        pointerEvents: exiting ? "none" : "auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        paddingTop: "max(env(safe-area-inset-top, 0px), 24px)",
        paddingBottom: "max(env(safe-area-inset-bottom, 0px), 32px)",
        paddingLeft: "24px",
        paddingRight: "24px",
        cursor: "pointer",
        color: "rgba(255,255,255,0.95)",
        minHeight: "100svh",
      }}
    >
      {/* ── Pillar-aperture black wall ── */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          background:
            // Near-black with subtle gallery depth: warm centre wash + dark vignette.
            "radial-gradient(ellipse 70% 55% at 50% 50%, #0c0a08 0%, #070605 55%, #030302 100%)",
          opacity: exiting ? 0 : 1,
          WebkitMaskImage: maskImage,
          maskImage: maskImage,
          WebkitMaskRepeat: "no-repeat",
          maskRepeat: "no-repeat",
          WebkitMaskSize: "100% 100%",
          maskSize: "100% 100%",
          // Center-out aperture: 2200ms slow opening. Overlay opacity only
          // fades in the FINAL ~700ms (delay 1500ms), so outer black edge
          // remains as a soft vignette while the centre is already revealing
          // the hero. This is reverse-vignette logic: never an even fade.
          transition: reduced
            ? `opacity 360ms ${easeSoft}`
            : `-webkit-mask-image 2200ms ${ease}, mask-image 2200ms ${ease}, opacity 700ms ${easeSoft} 1500ms`,
          willChange: "mask-image, opacity",
        }}
      />

      {/* Inner vignette — adds depth on top of the wall, also masked so it
          dissolves with the centre and lingers at the edges. */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          boxShadow: "inset 0 0 240px 70px rgba(0,0,0,0.6)",
          opacity: exiting ? 0 : 1,
          WebkitMaskImage: maskImage,
          maskImage: maskImage,
          transition: reduced
            ? `opacity 360ms ${easeSoft}`
            : `-webkit-mask-image 2200ms ${ease}, mask-image 2200ms ${ease}, opacity 700ms ${easeSoft} 1500ms`,
        }}
      />

      {/* Warm light bloom — grows at the moment of exit, like a gallery
          coming alive behind the wall. Restrained, never decorative. */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background:
            "radial-gradient(ellipse 60% 45% at 50% 52%, rgba(255, 226, 188, 0.10) 0%, transparent 70%)",
          opacity: exiting ? 0.0 : (visible ? 1 : 0),
          transform: exiting ? "scale(1.35)" : "scale(1)",
          transition: reduced
            ? `opacity 360ms ${easeSoft}`
            : `opacity 2000ms ${easeSoft}, transform 2200ms ${ease}`,
        }}
      />

      {/* ── Wordmark + subtitle + CTA ── */}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          maxWidth: "min(820px, 94vw)",
          gap: "clamp(16px, 2.6vh, 30px)",
          opacity: exiting ? 0 : 1,
          transform: exiting ? "translateY(-6px)" : "translateY(0)",
          transition: reduced
            ? `opacity 220ms ${easeSoft}`
            : `opacity 600ms ${easeSoft}, transform 800ms ${ease}`,
        }}
      >
        {/* Wordmark — bigger, navbar-style */}
        <h1
          className="font-display"
          style={{
            margin: 0,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.22em",
            // Larger, navbar-confident wordmark — gallery scale.
            fontSize: "clamp(2rem, 6.6vw, 5.2rem)",
            lineHeight: 1.05,
            color: "rgba(255,255,255,0.97)",
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(18px)",
            filter: visible ? "blur(0px)" : "blur(10px)",
            transition: reduced
              ? `opacity 360ms ${easeSoft}`
              : `opacity 1700ms ${ease} 180ms, transform 1700ms ${ease} 180ms, filter 1700ms ${ease} 180ms`,
            textShadow: "0 1px 30px rgba(0,0,0,0.5)",
          }}
        >
          Frank Pistorius
        </h1>

        {/* Subtitle */}
        <p
          style={{
            margin: 0,
            fontSize: "clamp(0.78rem, 1.6vw, 0.95rem)",
            letterSpacing: "0.36em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.66)",
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(12px)",
            filter: visible ? "blur(0px)" : "blur(7px)",
            transition: reduced
              ? `opacity 320ms ${easeSoft} 80ms`
              : `opacity 1200ms ${ease} 700ms, transform 1200ms ${ease} 700ms, filter 1200ms ${ease} 700ms`,
          }}
        >
          {copy.line1}
        </p>

        {/* CTA — rounded pill matching site button language */}
        <button
          type="button"
          autoFocus
          onClick={(e) => { e.stopPropagation(); handleEnter(); }}
          aria-label={copy.cta}
          className="entrance-cta"
          style={{
            marginTop: "clamp(28px, 5.5vh, 56px)",
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(12px)",
            transition: reduced
              ? `opacity 320ms ${easeSoft} 160ms`
              : `opacity 1100ms ${ease} 1300ms, transform 1100ms ${ease} 1300ms`,
          }}
        >
          <span className="entrance-cta__label">{copy.cta}</span>
        </button>
      </div>

      <style>{`
        .entrance-cta {
          position: relative;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 52px;
          padding: 14px 34px;
          border-radius: 9999px;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.28);
          color: rgba(255,255,255,0.94);
          font-size: clamp(0.82rem, 1.4vw, 0.95rem);
          letter-spacing: 0.28em;
          text-transform: uppercase;
          cursor: pointer;
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          transition:
            background-color 280ms ${easeSoft},
            border-color 280ms ${easeSoft},
            box-shadow 320ms ${easeSoft},
            transform 220ms cubic-bezier(0.2, 0, 0, 1);
          outline: none;
        }
        .entrance-cta::after {
          content: "";
          position: absolute;
          inset: 0;
          border-radius: 9999px;
          box-shadow: 0 0 0 0 rgba(255,236,210,0);
          transition: box-shadow 360ms ${easeSoft};
          pointer-events: none;
        }
        .entrance-cta:hover {
          background: rgba(255,236,210,0.08);
          border-color: rgba(255,236,210,0.55);
        }
        .entrance-cta:hover::after {
          box-shadow: 0 0 32px 2px rgba(255,226,188,0.18), inset 0 0 24px rgba(255,226,188,0.08);
        }
        .entrance-cta:active {
          transform: scale(0.985);
        }
        .entrance-cta:focus-visible {
          border-color: rgba(255,236,210,0.7);
          box-shadow: 0 0 0 2px rgba(255,236,210,0.25);
        }
      `}</style>
    </div>
  );
};

export default GalleryEntrance;
